import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListSites } from 'src/features/sites/api/useListSites';
import CreateSiteFormModal from 'src/features/sites/components/CreateSiteFormModal';
import CellSiteActions from 'src/features/sites/components/CellSiteActions';
import { Site } from 'src/features/sites/utils/types';
import { siteBadgeOptions } from 'src/features/sites/utils/constants';
import { siteMessages } from 'src/features/sites/utils/messages';
import { CellBadge } from 'src/registry/components/table/cells/CellBadge';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useTitleStore } from 'src/stores/useTitleStore';
import { generalMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/registry/hooks/usePagination';
import { PERMISSION } from 'src/features/auth';
import { booleanBadgeOptions } from 'src/utils/constants';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<Site>();

function Sites() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const manageSites = useHasPermissions([PERMISSION.MANAGE_SITES]);

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, parkingId, queryPaginationOptions],
    );

    const { data, isLoading, isError } = useListSites(payload);
    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(siteMessages.sites));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => row.original.translations.find((translation) => translation.language === locale)?.name,
            meta: {
                title: formatMessage(generalMessages.name),
            },
            id: 'name',
        }),
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: ({ row }) =>
                row.original.translations.find((translation) => translation.language === locale)?.address,
            meta: {
                title: formatMessage(siteMessages.address),
            },
            id: 'address',
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(siteMessages.type),
                badgeOptions: siteBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('floors', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(siteMessages.floorsCount),
            },
        }),
        columnHelper.accessor('is_24_7', {
            header: DataTableColumnHeader,
            cell: CellBoolean,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(siteMessages.is247),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellSiteActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Site, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(siteMessages.sites),
                entryName: formatMessage(siteMessages.site).toLowerCase(),
                entryNames: formatMessage(siteMessages.pluralSites).toLowerCase(),
                placeholder: formatMessage(siteMessages.site),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(manageSites && { newEntry: CreateSiteFormModal }),
            }}
        />
    );
}

export default Sites;
